/** @jsxImportSource react */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { api } from '../services/api';
import { useTranslation } from '../i18n';
import { Notification as NotificationType, User, UserRole } from '../types';
import { Bell, Mail } from 'lucide-react';

const getFormattedTime = (dateString: string, t: (key: string, options?: any) => string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return t('just_now', { defaultValue: 'Just now'});
    if (diffMins < 60) return t('min_ago', { defaultValue: `${diffMins}m ago`});
    if (diffHours < 24) return t('h_ago', { defaultValue: `${diffHours}h ago`});
    if (diffDays < 7) return t('d_ago', { defaultValue: `${diffDays}d ago`});
    return date.toLocaleDateString();
};

export const NotificationsPanel = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    const { data: notifications = [] } = useQuery({
        queryKey: ['notifications'],
        queryFn: api.getNotifications,
        enabled: !!user,
        refetchInterval: 60000,
    });
    
    // Only fetch users if the current user is Admin or Staff
    const canViewUsers = user?.role === UserRole.Admin || user?.role === UserRole.Staff;
    const { data: users = [] } = useQuery({
        queryKey: ['users'],
        queryFn: api.getUsers,
        enabled: !!user && canViewUsers,
    });

    const userMap = useMemo(() => {
        return users.reduce((acc, u) => {
            acc[u.id] = u;
            return acc;
        }, {} as Record<string, User>);
    }, [users]);

    const getNotificationText = (notification: NotificationType) => {
        if (!notification.messageKey) return notification.message;
        
        const params = { ...notification.messageParams };
        if (notification.messageKey.includes('orderStatusUpdate') && params.status) {
            params.status = t(String(params.status).toLowerCase());
        }
        
        return t(notification.messageKey, params);
    };

    const markSingleAsReadMutation = useMutation({
        mutationFn: (id: string) => api.markNotificationAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: () => api.markNotificationsAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Prevent body scroll on mobile
            if (window.innerWidth < 768) { // 'md' breakpoint
                document.body.style.overflow = 'hidden';
            }
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleNotificationClick = (notification: NotificationType) => {
        if (!notification.isRead) {
            markSingleAsReadMutation.mutate(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
        }
        setIsOpen(false);
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const getSenderName = (senderId?: string) => {
        if (!senderId || senderId === 'system') return t('systemSender', { defaultValue: 'System' });
        
        // If we have the user map (Admin/Staff), return the name
        if (userMap[senderId]) return userMap[senderId].name;
        
        // If current user is a client, they can't see other users, so if sender is not system, it's likely Support
        if (user?.role === UserRole.Client) return t('supportTeam', { defaultValue: 'Support Team' });
        
        return t('unknownSender', { defaultValue: 'Unknown' });
    };

    return (
        <div className="relative" ref={panelRef}>
            <button onClick={() => setIsOpen(prev => !prev)} className="relative p-2 text-secondary-500 hover:text-primary-600 hover:bg-background-off rounded-full transition-colors">
                <Bell size={22} />
                {unreadCount > 0 && (
                    <span className="absolute top-0 end-0 block h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
                )}
            </button>

            {isOpen && (
                <div className="fixed top-20 inset-x-4 mx-auto max-w-md md:absolute md:top-full md:mt-2 md:end-0 md:left-auto md:w-96 md:max-w-none bg-white rounded-xl shadow-2xl z-50 border border-secondary-100 overflow-hidden animate-fade-in">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="font-semibold text-secondary-800">{t('notifications')}</h3>
                        {unreadCount > 0 && (
                            <button 
                                onClick={() => markAllAsReadMutation.mutate()}
                                className="text-xs font-medium text-primary-600 hover:text-primary-800"
                                disabled={markAllAsReadMutation.isPending}
                            >
                                {t('markAllAsRead')}
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`flex items-start gap-3 p-4 hover:bg-background-off cursor-pointer ${!notification.isRead ? 'bg-primary-50/50' : ''}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                                        <Mail size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-secondary-800">
                                            <span className="font-semibold">{getSenderName(notification.senderId)}:</span> {getNotificationText(notification)}
                                        </p>
                                        <p className="text-xs text-secondary-500 mt-1">{getFormattedTime(notification.createdAt, t)}</p>
                                    </div>
                                    {!notification.isRead && <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />}
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center">
                                <Bell className="mx-auto h-10 w-10 text-secondary-300" />
                                <p className="mt-4 text-sm font-medium text-secondary-700">{t('no_notifications_yet')}</p>
                                <p className="mt-1 text-xs text-secondary-500">{t('check_back_later')}</p>
                            </div>
                        )}
                    </div>
                    <div className="p-2 border-t bg-secondary-50/50">
                        <button
                            onClick={() => { navigate('/notifications'); setIsOpen(false); }}
                            className="w-full text-center text-sm font-medium text-primary-600 hover:text-primary-800 py-2 rounded-md hover:bg-primary-100"
                        >
                            {t('viewAllNotifications')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};